import axios from 'axios';
import { AuthResponse, RegisterData, User } from '../types/auth';
import { isCaptchaSiteKeyConfigured } from '../utils/captcha';
import i18n from '../i18n';

const IS_DEV = process.env.NODE_ENV !== 'production';
const ALLOW_TEST_AUTH =
  process.env.REACT_APP_ALLOW_TEST_AUTH === 'true' ||
  (process.env.REACT_APP_ALLOW_TEST_AUTH !== 'false' && IS_DEV);
const debugLog = (...args: unknown[]) => {
  if (IS_DEV) {
    console.log(...args);
  }
};
const debugWarn = (...args: unknown[]) => {
  if (IS_DEV) {
    console.warn(...args);
  }
};

// Динамично определяне на API URL
// Ако се зарежда от браузъра, използваме hostname от URL-а
const getApiUrl = () => {
  // Проверяваме дали сме в браузъра
  if (typeof window !== 'undefined') {
    const { protocol, hostname, port } = window.location;

    // Локален dev: по подразбиране винаги удряме локалния backend.
    // Ако ти трябва override, задай REACT_APP_RESPECT_LOCAL_API_URL=true.
    const isLocalDev = hostname === 'localhost' || hostname === '127.0.0.1';
    if (isLocalDev && port === '3000') {
      if (process.env.REACT_APP_RESPECT_LOCAL_API_URL === 'true' && process.env.REACT_APP_API_URL) {
        return process.env.REACT_APP_API_URL;
      }
      return 'http://localhost:8000';
    }

    if (process.env.REACT_APP_API_URL) {
      return process.env.REACT_APP_API_URL;
    }

    // В production зад reverse proxy използваме същия origin.
    return `${protocol}//${hostname}${port ? `:${port}` : ''}`;
  }
  // Fallback за SSR или Node среда
  return 'http://localhost:8000';
};

const API_URL = getApiUrl();

// Функция за преобразуване на ID от низ в число за API заявки
export const normalizeId = (id: string | number | undefined): number => {
  if (id === undefined) return 0;
  return typeof id === 'string' ? parseInt(id, 10) : id;
};

// Създаваме axios инстанция с withCredentials
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  },
  withCredentials: false
});

// Интерцептор за добавяне на токън към заявките
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  
  // Извличаме ID от URL и го преобразуваме в число, ако е необходимо
  if (config.url) {
    // Преобразуваме ID в URL шаблони като /api/v1/clients/123
    const urlPattern = /\/api\/v1\/(clients|professionals|users|notifications|me)\/([^\/\?]+)/;
    const match = config.url.match(urlPattern);
    
    if (match && match[2]) {
      const entityType = match[1]; // clients или professionals
      const entityId = match[2];
      
      // Преобразуваме ID в число и обновяваме URL
      // НЕ преобразуваме 'me' в число
      if (entityType !== 'me') {
        const numericId = normalizeId(entityId);
        config.url = config.url.replace(
          `/${entityType}/${entityId}`, 
          `/${entityType}/${numericId}`
        );
        debugLog('Normalized API URL:', config.url);
      }
    }
  }
  
  // Преобразуване на ID полета в request data, ако е необходимо
  if (config.data && typeof config.data === 'object') {
    // Ако данните са обект, преобразуваме всички ID полета в числа
    try {
      // За заявки с тяло като JSON
      if (config.data.id && typeof config.data.id === 'string') {
        config.data.id = normalizeId(config.data.id);
      }

      if (config.data.user_id && typeof config.data.user_id === 'string') {
        config.data.user_id = normalizeId(config.data.user_id);
      }

      // Ако има notification_settings, запазваме като обект, а не като string
      if (config.data.notification_settings && typeof config.data.notification_settings === 'object') {
        // Запазваме обекта, но не правим промени в него
        config.data.notification_settings = {
          ...config.data.notification_settings
        };
      }

      debugLog('Normalized request data:', config.data);
    } catch (error) {
      console.error('Error normalizing request data:', error);
    }
  }
  
  debugLog('Request:', config.method, config.url, config.headers, config.baseURL);
  return config;
});

// Интерцептор за обработка на грешки
api.interceptors.response.use(
  (response) => {
    debugLog('Response:', response.status, response.config.url, response.data);
    return response;
  },
  (error) => {
    const statusCode = error?.response?.status;
    const requestUrl = error?.config?.url || '';
    const isNotificationsAuthError =
      /\/api\/v1\/notifications/.test(requestUrl) &&
      (statusCode === 401 || statusCode === 403);
    const isExpectedProfileNotFound =
      statusCode === 404 &&
      /\/api\/v1\/(clients|professionals)\/\d+(\?.*)?$/.test(requestUrl);

    if (error.response?.status === 401) {
      debugLog('401 Unauthorized error detected');
      
      // Запазваме текущия URL за по-късно пренасочване след логин
      if (window.location.pathname !== '/login') {
        const currentPath = window.location.pathname;
        debugLog('Saving current path for redirect:', currentPath);
        localStorage.setItem('redirectAfterLogin', currentPath);
      }
    }

    if (isExpectedProfileNotFound) {
      throw error;
    }

    if (isNotificationsAuthError) {
      throw error;
    }

    if (error.response?.data) {
      console.error('API Error:', error.response.status, error.response.data);
      console.error('Failed request URL:', error.config?.url);
      console.error('Failed request method:', error.config?.method);
    } else if (error.request) {
      // Заявката е направена, но не е получен отговор
      console.error('API Network Error:', error.message);
      console.error('Request details:', {
        url: error.config?.url,
        method: error.config?.method,
        baseURL: error.config?.baseURL
      });
      
      // Проверка дали сървърът е стартиран
      console.error('Possible cause: Backend server is not running or not accessible at', API_URL);
      console.error('You may need to start the backend server or use mock data.');
    } else {
      // Възникнала е грешка при настройката на заявката
      console.error('API Request Error:', error.message);
    }
    throw error;
  }
);

// Функция за трансформиране на данните от бекенда
const transformUserData = (data: any): User => {
  return {
    id: data.id,
    email: data.email,
    full_name: data.full_name || data.name,
    username: data.username,
    is_active: data.is_active,
    is_superuser: data.is_superuser,
    user_type: data.user_type,
    created_at: data.created_at,
    role: data.role,
    email_verified_at: data.email_verified_at
  };
};

export const authApi = {
  login: async (email: string, password: string, captchaToken?: string): Promise<AuthResponse> => {
    try {
      const captchaUiEnabled = isCaptchaSiteKeyConfigured();

      const allLoginCandidates: Array<{
        url: string;
        headers: Record<string, string>;
        data: any;
      }> = [
        {
          url: `${API_URL}/api/v1/auth/json-login`,
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          data: { email, password }
        },
        {
          url: `${API_URL}/api/v1/auth/login/access-token`,
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            'Accept': 'application/json'
          },
          data: new URLSearchParams({
            username: email,
            password
          })
        },
        {
          url: `${API_URL}/api/v1/login/access-token`,
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            'Accept': 'application/json'
          },
          data: new URLSearchParams({
            username: email,
            password
          })
        }
      ];

      const loginCandidates = captchaUiEnabled
        ? allLoginCandidates.filter((candidate) => candidate.url.includes('/auth/json-login'))
        : allLoginCandidates;

      let lastError: any = null;
      for (const candidate of loginCandidates) {
        if (candidate.url.includes('/auth/json-login')) {
          candidate.data = { ...candidate.data, captcha_token: captchaToken };
        }
        try {
          const response = await axios({
            method: 'post',
            url: candidate.url,
            headers: candidate.headers,
            withCredentials: false,
            data: candidate.data
          });
          return response.data;
        } catch (candidateError) {
          lastError = candidateError;
          debugWarn(`Login endpoint failed: ${candidate.url}`, candidateError);
        }
      }

      throw lastError;
    } catch (error: any) {
      console.error('Login error:', error);
      console.error('Login error details:', error.response?.data);
      throw error;
    }
  },

  register: async (data: RegisterData): Promise<{ user: User; access_token: string; token_type: string }> => {
    try {
      // Опростени данни за регистрация
      const userData = {
        email: data.email.trim().toLowerCase(),
        password: data.password,
        full_name: data.full_name,
        user_type: data.user_type,
        username: data.email.trim().toLowerCase(),
        captcha_token: data.captcha_token,
        accepted_terms: data.accepted_terms,
        // Текущо избраният UI език - за да получава потребителят имейли
        // (verification код, reset на парола...) на своя език, а не само на BG.
        language: i18n.language,
      };
      debugLog('Sending registration data:', userData);
      
      const registerCandidates = [
        '/api/v1/auth/signup',
        '/api/v1/signup'
      ];

      let lastError: any = null;
      for (const endpoint of registerCandidates) {
        try {
          const response = await api.post(endpoint, userData);
          debugLog('Registration successful:', response.data);
          // /auth/signup връща access_token директно - не правим втора
          // login заявка (CAPTCHA token-ът е еднократен и вече е използван).
          return {
            user: transformUserData(response.data.user ?? response.data),
            access_token: response.data.access_token,
            token_type: response.data.token_type,
          };
        } catch (candidateError) {
          lastError = candidateError;
          debugWarn(`Register endpoint failed: ${endpoint}`, candidateError);
        }
      }

      throw lastError;
    } catch (error) {
      console.error('Registration error:', error);
      if (axios.isAxiosError(error)) {
        console.error('Registration error details:', error.response?.data);
        console.error('Registration error status:', error.response?.status);
        console.error('Registration error headers:', error.response?.headers);
      }
      throw error;
    }
  },

  getCurrentUser: async (): Promise<User> => {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('No token found');
    }
    
    debugLog('getCurrentUser: Making API request to /api/v1/me');
    
    // Декодираме токена преди да направим заявката
    try {
      const parts = token.split('.');
      if (parts.length === 3) {
        const payload = JSON.parse(atob(parts[1]));
        debugLog('Token payload in getCurrentUser:', payload);
      }
    } catch (e) {
      console.error('Failed to decode token:', e);
    }
    
    try {
      // Опитваме да вземем данни от /api/v1/me
      const response = await api.get('/api/v1/me');
      debugLog('getCurrentUser: Raw response data:', response.data);
      
      const userData = transformUserData(response.data);
      debugLog('getCurrentUser: Transformed user data:', userData);
      debugLog('Setting user_type in localStorage:', userData.user_type);
      if (userData.user_type) {
        localStorage.setItem('user_type', userData.user_type);
      }
      
      return userData;
    } catch (error) {
      if (!ALLOW_TEST_AUTH) {
        throw error;
      }

      // Fallback - само за development/test режим.
      debugWarn('/api/v1/me failed, using fallback mock data:', error);
      
      // Декодираме токена за да вземем user ID
      const token = localStorage.getItem('token');
      let userId = 1;
      let userEmail = 'test@example.com';
      
      if (token) {
        try {
          const parts = token.split('.');
          if (parts.length === 3) {
            const payload = JSON.parse(atob(parts[1]));
            userId = parseInt(payload.sub) || 1;
            debugLog('Extracted user ID from token:', userId);
          }
        } catch (e) {
          console.error('Failed to decode token for fallback:', e);
        }
      }
      
      // Използваме email от localStorage ако има
      const storedEmail = localStorage.getItem('current_email');
      if (storedEmail) {
        userEmail = storedEmail;
      }
      
      // Определяме user_type базиран на email
      let userType = 'professional';
      if (userEmail.includes('test67') || userEmail.includes('client')) {
        userType = 'client';
      }
      
      // Връзваме mock потребител базиран на token и email
      const mockUser: User = {
        id: userId,
        email: userEmail,
        full_name: userEmail.includes('test55') ? 'Професионалист 55' : 
                   userEmail.includes('test67') ? 'Клиент 67' : 'Test User',
        username: userEmail,
        is_active: true,
        is_superuser: false,
        user_type: userType,
        avatar_url: '',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        phone: '',
        location: '',
        bio: '',
        rating: 0,
        unread_messages: 0,
      };
      
      debugLog('Using fallback user:', mockUser);
      if (mockUser.user_type) {
        localStorage.setItem('user_type', mockUser.user_type);
      }
      
      return mockUser;
    }
  },
};

export const clientApi = {
  // Обновяване на профил чрез /api/v1/me endpoint (директно текущия потребител)
  updateClientProfile: async (userId: string | number, data: any): Promise<any> => {
    try {
      debugLog('Updating user profile:', userId, data);
      
      // Опитваме да обновим директно профила чрез /api/v1/me ендпойнта
      // Този ендпойнт следва да работи за текущия потребител
      try {
        debugLog('Trying to update current user profile directly');
        
        // Използваме name вместо full_name
        const userData: Record<string, any> = {
          name: data.name,
          email: data.email,
          phone: data.phone,
          location: data.location,
          avatar_url: data.avatar_url
        };

        // notification_settings се игнорираше досега - тук изрично го пренасяме,
        // за да се персистира реално (вместо да остане само декоративно в UI).
        if (data.notification_settings) {
          userData.notification_settings = data.notification_settings;
        }
        
        debugLog('Sending update to /api/v1/me with:', userData);
        
        // Изпращаме заявка към me endpoint за обновяване
        const response = await api.patch('/api/v1/me', userData);
        debugLog('Profile update successful:', response.data);
        
        return response.data;
      } catch (updateError) {
        console.error('Failed to update profile:', updateError);
        throw updateError;
      }
    } catch (error) {
      console.error('Profile update error:', error);
      throw error;
    }
  }
};

// File Upload API
export const fileApi = {
  // Качва файл
  uploadFile: async (file: File, taskId?: number, messageId?: number) => {
    const formData = new FormData();
    formData.append('file', file);
    if (taskId) formData.append('task_id', taskId.toString());
    if (messageId) formData.append('message_id', messageId.toString());

    const response = await api.post('/api/v1/files/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  // Връща файлове за задача
  getTaskFiles: async (taskId: number) => {
    const response = await api.get(`/api/v1/files/task/${taskId}`);
    return response.data;
  },

  // Връща файлове за съобщение
  getMessageFiles: async (messageId: number) => {
    const response = await api.get(`/api/v1/files/message/${messageId}`);
    return response.data;
  },

  // Изтрива файл
  deleteFile: async (fileId: number) => {
    await api.delete(`/api/v1/files/${fileId}`);
  },
};

// Messages API
export const messagesApi = {
  // Връща всички разговори за текущия потребител
  getConversations: async () => {
    const response = await api.get('/api/v1/chat-messages/conversations');
    return response.data;
  },
};

// Best-effort персистване на избрания език в профила на logged-in потребител,
// за да бъдат бъдещите email-и (reset на парола и т.н.) на неговия език.
// Не хвърля грешка навън - смяната на езика в UI трябва да работи дори ако
// заявката се провали (напр. не сме логнати).
export const persistUserLanguage = async (language: string): Promise<void> => {
  const token = localStorage.getItem('token');
  if (!token) return;
  try {
    await api.patch('/api/v1/me', { language });
  } catch (error) {
    debugWarn('Failed to persist language preference:', error);
  }
};

export default api; 